import React, { useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { X, Upload, FileText, Download, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

/**
 * iter72h · BulkImportContactsModal
 *
 * Lets staff paste OR drop a CSV of vendor contacts and import them into
 * the Club Vendor Directory in one shot. Two-phase flow:
 *   1. Parse CSV client-side, POST rows → server inserts clean rows,
 *      returns any duplicates.
 *   2. Staff resolves each duplicate (skip / overwrite), POST again with
 *      a resolutions map — server applies and returns final summary.
 */

const HEADERS = [
  "vendor_company_name", "representative_name", "representative_email",
  "representative_title", "representative_phone", "brand", "category",
  "territory", "website", "notes",
];
const REQUIRED = new Set(["vendor_company_name", "representative_name", "representative_email"]);

export default function BulkImportContactsModal({ open, onClose, onImported }) {
  const [csv, setCsv] = useState("");
  const [parseErr, setParseErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("input"); // "input" | "resolve" | "done"
  const [duplicates, setDuplicates] = useState([]);
  const [resolutions, setResolutions] = useState({});
  const [summary, setSummary] = useState(null);
  const [serverErr, setServerErr] = useState("");
  const fileRef = useRef();

  const parsed = useMemo(() => (csv.trim() ? _parseCsv(csv) : { rows: [], errors: [] }), [csv]);

  const reset = () => {
    setCsv(""); setParseErr(""); setPhase("input"); setDuplicates([]);
    setResolutions({}); setSummary(null); setServerErr("");
  };

  const handleClose = () => { reset(); onClose?.(); };

  const submit = async () => {
    setParseErr(""); setServerErr("");
    if (!parsed.rows.length) { setParseErr("No valid rows found in the CSV."); return; }
    if (parsed.errors.length) {
      setParseErr(`Fix these CSV issues first: ${parsed.errors.slice(0, 3).join(" · ")}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/club/vendor-contacts/bulk-import", {
        rows: parsed.rows,
      });
      setSummary(data.summary);
      if ((data.duplicates || []).length > 0) {
        setDuplicates(data.duplicates);
        // Default every dupe to "skip" so staff has to opt into overwrite.
        const init = {};
        for (const d of data.duplicates) init[d.email_normalized] = "skip";
        setResolutions(init);
        setPhase("resolve");
      } else {
        setPhase("done");
        onImported?.();
      }
    } catch (e) {
      setServerErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const submitResolutions = async () => {
    setBusy(true); setServerErr("");
    try {
      // Only send the duplicate rows back — clean rows already inserted.
      const rows = duplicates.map((d) => d.csv_row);
      const { data } = await api.post("/club/vendor-contacts/bulk-import", {
        rows,
        resolutions,
      });
      // Merge counts into the phase-1 summary.
      setSummary((prev) => ({
        inserted: (prev?.inserted || 0) + data.summary.inserted,
        updated: (prev?.updated || 0) + data.summary.updated,
        skipped: (prev?.skipped || 0) + data.summary.skipped,
        duplicates_pending: 0,
        total_rows: (prev?.total_rows || 0),
      }));
      setPhase("done");
      onImported?.();
    } catch (e) {
      setServerErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/club/vendor-contacts/bulk-import/template");
      const blob = new Blob([data.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "vendor-contacts-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsv(text);
    if (fileRef.current) fileRef.current.value = "";
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={handleClose}
      data-testid="bulk-import-modal"
    >
      <div
        className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-[#E5E3D8]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 border-b border-[#E5E3D8] flex items-start justify-between gap-3">
          <div>
            <div className="overline text-[#B59A5A]">Bulk Import</div>
            <h2 className="font-serif text-3xl text-[#1A422D] mt-1">Vendor Contact Import</h2>
            <p className="text-xs text-[#7A7A7A] mt-1">
              Seed your entire rep book in one shot — paste a CSV or drop a file.
            </p>
          </div>
          <button type="button" onClick={handleClose} data-testid="bulk-import-close"
                  className="text-[#7A7A7A] hover:text-[#1A422D] text-3xl leading-none">×</button>
        </div>

        <div className="p-8">
          {phase === "input" && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="bulk-import-file"
                  className="inline-flex items-center gap-2 border border-[#1A422D] text-[#1A422D] hover:bg-[#1A422D] hover:text-[#F9F8F4] px-4 py-2 text-[11px] uppercase tracking-[0.22em]"
                >
                  <Upload size={13} /> Upload .csv file
                </button>
                <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
                <button
                  type="button"
                  onClick={downloadTemplate}
                  data-testid="bulk-import-template"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#8a6f33] hover:text-[#1A422D]"
                >
                  <Download size={13} /> Download template
                </button>
              </div>

              <div className="mt-4">
                <label className="label-elegant block mb-1.5">Or paste CSV directly</label>
                <textarea
                  value={csv}
                  onChange={(e) => setCsv(e.target.value)}
                  data-testid="bulk-import-textarea"
                  rows={10}
                  spellCheck={false}
                  className="input-field font-mono text-xs w-full"
                  placeholder="vendor_company_name,representative_name,representative_email,..."
                />
              </div>

              {parsed.rows.length > 0 && (
                <div className="mt-3 text-xs text-[#4A4A4A]" data-testid="bulk-import-preview">
                  Ready to import <strong className="text-[#1A422D]">{parsed.rows.length}</strong> contact{parsed.rows.length === 1 ? "" : "s"}.
                  {parsed.errors.length > 0 && (
                    <span className="text-[#8a1a1a]">
                      {" "}· {parsed.errors.length} row{parsed.errors.length === 1 ? "" : "s"} skipped
                    </span>
                  )}
                </div>
              )}
              {parsed.errors.length > 0 && (
                <ul className="mt-2 text-[11px] text-[#8a1a1a] space-y-0.5 list-disc pl-5" data-testid="bulk-import-errors">
                  {parsed.errors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                  {parsed.errors.length > 5 && (
                    <li className="text-[#7A7A7A]">…and {parsed.errors.length - 5} more.</li>
                  )}
                </ul>
              )}
              {parseErr && (
                <div className="mt-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-xs flex items-center gap-2">
                  <AlertTriangle size={12} /> {parseErr}
                </div>
              )}
              {serverErr && (
                <div className="mt-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-xs flex items-center gap-2">
                  <AlertTriangle size={12} /> {serverErr}
                </div>
              )}

              <div className="mt-6 flex items-center justify-end gap-3">
                <button type="button" onClick={handleClose} className="text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] hover:text-[#1A422D] px-3 py-2">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy || !parsed.rows.length}
                  data-testid="bulk-import-submit"
                  className="inline-flex items-center gap-2 bg-[#1A422D] text-[#F9F8F4] px-5 py-2 text-[11px] uppercase tracking-[0.22em] disabled:opacity-50 hover:bg-[#0f1a14]"
                >
                  {busy && <Loader2 size={12} className="animate-spin" />}
                  {busy ? "Importing…" : `Import ${parsed.rows.length || 0} Contact${parsed.rows.length === 1 ? "" : "s"}`}
                </button>
              </div>
            </>
          )}

          {phase === "resolve" && (
            <>
              <div className="border border-[#B59A5A] bg-[#FBF6E8] px-3 py-2 text-xs text-[#8a6f33] flex items-center gap-2">
                <AlertTriangle size={12} />
                Clean rows imported: <strong>{summary?.inserted || 0}</strong>. Resolve {duplicates.length} duplicate{duplicates.length === 1 ? "" : "s"} below.
              </div>
              <div className="mt-4 space-y-3" data-testid="bulk-import-dupes-list">
                {duplicates.map((d) => {
                  const r = d.csv_row;
                  const key = d.email_normalized;
                  const choice = resolutions[key] || "skip";
                  return (
                    <div key={key} className="border border-[#E5E3D8] p-4"
                         data-testid={`bulk-dupe-${key.replace(/[^a-z0-9]/gi, "_")}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                        <div>
                          <div className="overline text-[#B59A5A]">CSV row</div>
                          <div className="font-serif text-sm text-[#1A422D] mt-0.5">{r.vendor_company_name}</div>
                          <div className="text-[#4A4A4A]">{r.representative_name}</div>
                          <div className="text-[#7A7A7A]">{r.representative_email}</div>
                        </div>
                        <div>
                          <div className="overline text-[#B59A5A]">Existing contact</div>
                          <div className="font-serif text-sm text-[#1A422D] mt-0.5">{d.existing?.vendor_company_name}</div>
                          <div className="text-[#4A4A4A]">{d.existing?.representative_name}</div>
                          <div className="text-[#7A7A7A]">{d.existing?.representative_email}</div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <ChoiceButton
                          active={choice === "skip"}
                          onClick={() => setResolutions((rs) => ({ ...rs, [key]: "skip" }))}
                          testId={`bulk-dupe-skip-${key.replace(/[^a-z0-9]/gi, "_")}`}
                        >Skip</ChoiceButton>
                        <ChoiceButton
                          active={choice === "overwrite"}
                          onClick={() => setResolutions((rs) => ({ ...rs, [key]: "overwrite" }))}
                          testId={`bulk-dupe-overwrite-${key.replace(/[^a-z0-9]/gi, "_")}`}
                          variant="warn"
                        >Overwrite with CSV row</ChoiceButton>
                      </div>
                    </div>
                  );
                })}
              </div>
              {serverErr && (
                <div className="mt-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-xs flex items-center gap-2">
                  <AlertTriangle size={12} /> {serverErr}
                </div>
              )}
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={submitResolutions}
                  disabled={busy}
                  data-testid="bulk-import-apply-resolutions"
                  className="inline-flex items-center gap-2 bg-[#1A422D] text-[#F9F8F4] px-5 py-2 text-[11px] uppercase tracking-[0.22em] disabled:opacity-50 hover:bg-[#0f1a14]"
                >
                  {busy && <Loader2 size={12} className="animate-spin" />}
                  {busy ? "Applying…" : "Apply resolutions"}
                </button>
              </div>
            </>
          )}

          {phase === "done" && summary && (
            <div className="text-center py-6" data-testid="bulk-import-summary">
              <CheckCircle2 size={32} className="mx-auto text-[#1A422D]" />
              <div className="overline text-[#B59A5A] mt-3">Import complete</div>
              <h3 className="font-serif text-2xl text-[#1A422D] mt-1">
                {summary.inserted} added
                {summary.updated > 0 && ` · ${summary.updated} updated`}
                {summary.skipped > 0 && ` · ${summary.skipped} skipped`}
              </h3>
              <p className="text-xs text-[#7A7A7A] mt-2">
                The directory has been refreshed.
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => { reset(); }}
                  className="text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] hover:text-[#1A422D] px-3 py-2"
                >
                  Import another
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  data-testid="bulk-import-done-close"
                  className="bg-[#1A422D] text-[#F9F8F4] px-5 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#0f1a14]"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChoiceButton({ active, onClick, testId, variant, children }) {
  const base = "px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] transition border";
  const cls = active
    ? (variant === "warn"
        ? "border-[#8a1a1a] bg-[#8a1a1a] text-white"
        : "border-[#1A422D] bg-[#1A422D] text-white")
    : "border-[#E5E3D8] text-[#4A4A4A] hover:border-[#1A422D] hover:text-[#1A422D]";
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={`${base} ${cls}`}>{children}</button>
  );
}

// ---- CSV parsing ---------------------------------------------------------
// Small, forgiving CSV reader — handles quoted fields, CRLF, and column
// aliasing (case-insensitive, whitespace-insensitive header match).
function _parseCsv(text) {
  const errors = [];
  const rows = [];
  const lines = _splitLines(text);
  if (!lines.length) return { rows, errors };
  const rawHeaders = _splitCsvLine(lines[0]);
  const normHeaders = rawHeaders.map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  const columnIndex = {};
  HEADERS.forEach((h) => {
    const idx = normHeaders.indexOf(h);
    if (idx >= 0) columnIndex[h] = idx;
  });
  const missing = [...REQUIRED].filter((h) => !(h in columnIndex));
  if (missing.length) {
    errors.push(`Missing required column(s): ${missing.join(", ")}`);
    return { rows, errors };
  }

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const cells = _splitCsvLine(line);
    const rowN = i + 1;
    const get = (h) => {
      const idx = columnIndex[h];
      return idx == null ? "" : (cells[idx] || "").trim();
    };
    const row = {};
    for (const h of HEADERS) {
      const v = get(h);
      if (v) row[h] = v;
    }
    const missReq = [...REQUIRED].filter((h) => !row[h]);
    if (missReq.length) {
      errors.push(`Row ${rowN}: missing ${missReq.join(", ")}`);
      continue;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.representative_email)) {
      errors.push(`Row ${rowN}: invalid email "${row.representative_email}"`);
      continue;
    }
    rows.push(row);
  }
  return { rows, errors };
}

function _splitLines(text) {
  return text.replace(/\r\n?/g, "\n").split("\n");
}

function _splitCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuote) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQuote = false; }
      else cur += ch;
    } else {
      if (ch === '"') inQuote = true;
      else if (ch === ",") { out.push(cur); cur = ""; }
      else cur += ch;
    }
  }
  out.push(cur);
  return out;
}
